import * as Yup from "yup";
import {
  formatToCurrency,
  isPhoneNumberValid,
  parsePhoneNumber,
  parseStringToNumber,
} from "../helperFunctions";
import { SingleMemberContributionDetails } from "../redux/features/cooperative/cooperative.type";
import { LoanProduct } from "../redux/features/loans/loans.type";
import { ApprovalChainMapValue } from "../utils/types";

export const loginSchema1 = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
});

export const loginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

export const cooperativeSignUpSchema = Yup.object().shape({
  cooperativeName: Yup.string().required("Name is required"),
  cooperativeRegNum: Yup.string().required("Registration number is required"),
  cooperativeEmail: Yup.string()
    .email("Invalid email")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
  cooperativeSize: Yup.array().min(1).required("Cooperative size is required"),
});

export const memberSignUpSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

export const confirmEmailSchema = Yup.object().shape({
  code: Yup.string().required("Code is required"),
});

export const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
});

export const createNewPasswordSchema = Yup.object().shape({
  code: Yup.string().required("Code is required"),
  password: Yup.string().required("Password is required"),
  newPassword: Yup.string()
    .required("Password is required")
    .oneOf([Yup.ref("password")], "Passwords do not match."),
});

export const changePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Password is required"),
  newPassword: Yup.string().required("Password is required"),
  confirmNewPassword: Yup.string()
    .required("Password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords do not match."),
});

export const CooperativeBankAccountSchema = Yup.object().shape({
  bankName: Yup.string().required("Bank is required"),
  bankCode: Yup.string().required("Bank is required"),
  accountNumber: Yup.string()
    .matches(/^[0-9]+$/, "Account number is invalid")
    .required("Account number is required")
    .min(10, "Account number must be exactly 10 digits")
    .max(10, "Account number must be exactly 10 digits"),
  accountName: Yup.string().required("Account name is required"),
});

export const inviteCooperativeOfficialsSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  role: Yup.object()
    .test("not-empty", "Role is required", (value) => {
      return Object.values(value)?.length > 0;
    })
    .required("Role is required"),
});

export const membershipFormSchema = Yup.object().shape({
  fee: Yup.string().required("Fee is required"),
  form: Yup.array().of(Yup.object()).required("Form is required"),
});

export const addMembershipFormFieldSchema = Yup.object().shape({
  fieldName: Yup.string()
    .required("Field name is required")
    .matches(
      /^[A-Za-z0-9 ]+$/,
      "Only letters, numbers, and spaces are allowed"
    ),
  fieldType: Yup.string().required("Field type is required"),
});

export const inviteMemberSchema = Yup.object().shape({
  email: Yup.array()
    .of(Yup.string().email("Invalid email").required("Required"))
    .min(1),
});

export const addSavingsSchema = Yup.object().shape({
  amount: Yup.string().required("Amount is required"),
});

export const addSavingsSchemaStep2 = Yup.object().shape({
  amount: Yup.string().required("Amount is required"),
  paymentMethod: Yup.string().required(),
});

export const addSavingsAdminSchema = Yup.object().shape({
  amount: Yup.string().required("Amount is required"),
  file: Yup.array()
    .min(1, "Upload proof of payment")
    .required("Upload proof of payment"),
  date: Yup.date().required("Provide payment date"),
});

export const profileUpdateSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  middleName: Yup.string(),
  lastName: Yup.string().required("Last name is required"),
  dateOfBirth: Yup.string().required("Date of Birth is required"),
  phoneNumber: Yup.string()
    .test("is-valid-phone", `Enter a valid phone number`, (value) => {
      const parsed = parsePhoneNumber(value || "")?.country ?? "NG";
      return isPhoneNumberValid(value || "", parsed);
    })
    .required("Required"),
  email: Yup.string().email().required("Email is required"),
  address: Yup.string().required("Address is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
});

export const depositConfigurationSchema = Yup.object().shape({
  withdrawalDays: Yup.string().required("Required"),
  depositsInterestRate: Yup.string().required("Required"),
});

export const sharesConfigurationSchema = Yup.object().shape({
  price: Yup.string().required("Required"),
  minAmount: Yup.string().required("Required"),
  sharesInterestRate: Yup.string().required("Required"),
});

export const addLoanProductSchema = Yup.object().shape({
  loanName: Yup.string().required("Loan name is required"),
  loanDescription: Yup.string().required("Loan description is required"),
  maxGuarantor: Yup.string().required("Required"),
  minMonthJoined: Yup.string().required("Required"),
  maxTenure: Yup.string().required("Required"),
});

export const extraLoanProductConfigurationSchema = Yup.object().shape({
  minLoanAmount: Yup.string().required("Minimum loan amount is required"),
  maxXAmount: Yup.string().required("Maximum loan amount is required"),
  maxInterestRate: Yup.string().required("Interest rate is required"),
  penaltyFee: Yup.string().required("Penalty fee is required"),
  loanApplicationFee: Yup.string().required("Loan processing fee is required"),
  multipleRunningLoan: Yup.boolean().required(),
  EnableDisbursement: Yup.boolean().required(),
  paymentMode: Yup.array()
    .of(Yup.string())
    .min(1, "Select at least one repayment method")
    .required("Select at least one repayment method"),
});

export const manageRoleSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  permissions: Yup.array()
    .of(Yup.string())
    .min(1)
    .required("Permissions are required"),
});

export const assignOfficialSchema = (email?: string) => {
  return Yup.object().shape({
    email: Yup.string()
      .notOneOf(
        [email],
        "This email is already assigned to the role. Please enter a different email."
      )
      .email("Enter a valid email")
      .required("Email is required"),
    agreed: Yup.boolean().oneOf([!!email ? true : false]),
  });
};

export const editApprovalChainSchema = (
  chainMapValue: ApprovalChainMapValue
) => {
  return Yup.object().shape({
    chain: Yup.array()
      .of(
        Yup.object().shape({
          rolePermissions: Yup.array()
            .of(Yup.string())
            .required("Permissions are required"),
          roleId: Yup.string().required("Role is required"),
        })
      )
      .min(2, "Minimum of two approval levels are required")
      .required("Approval chain is required")
      .test(
        "custom-validation-per-index",
        "Invalid chain configuration",
        function (chain) {
          if (!Array.isArray(chain)) return false;

          const first = chain[0];
          if (
            !first ||
            !first.rolePermissions ||
            !chainMapValue.initiatorPermission.every((p) =>
              first.rolePermissions.includes(p)
            )
          ) {
            return this.createError({
              path: `chain[0].roleId`,
              message: `Approver 1 must have permissions: ${chainMapValue.initiatorPermission
                .map((v) => v.split(".").join(" "))
                .join(", ")}`,
            });
          }

          for (let i = 1; i < chain.length; i++) {
            const item = chain[i];
            if (
              !item ||
              !item.roleId ||
              !item.rolePermissions ||
              !chainMapValue.approverPermissions.every((p) =>
                item.rolePermissions.includes(p)
              )
            ) {
              return this.createError({
                path: `chain[${i}].roleId`,
                message: `Approver ${
                  i + 1
                } must have permissions: ${chainMapValue.approverPermissions
                  .map((v) => v.split(".").join(" "))
                  .join(", ")}`,
              });
            }
          }

          return true;
        }
      ),
  });
};

export const uploadContributionSchema = Yup.object().shape({
  date: Yup.string().required("Date is required"),
  time: Yup.string().required("Time is required"),
  amount: Yup.string().required("Amount is required"),
  file: Yup.mixed().required("File is required"),
  method: Yup.string().required("Payment method is required"),
});

export const bulkUploadContributionSchema = Yup.object().shape({
  identifier: Yup.string().required("identifier is required"),
  file: Yup.mixed().required("File is required"),
});

export const uploadDepositSchema = Yup.object().shape({
  date: Yup.string().required("Date is required"),
  time: Yup.string().required("Time is required"),
  amount: Yup.string().required("Amount is required"),
  file: Yup.mixed().required("File is required"),
  method: Yup.string().required("Payment method is required"),
});

export const fileUploadSchema = Yup.object().shape({
  file: Yup.mixed().required("File is required"),
});

export const bulkUploadNameSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
});

export const bulkUploadDepositSchema = Yup.object().shape({
  identifier: Yup.string().required("identifier is required"),
  file: Yup.mixed().required("File is required"),
});

export const uploadShareSchema = Yup.object().shape({
  date: Yup.string().required("Date is required"),
  time: Yup.string().required("Time is required"),
  unit: Yup.string().required("Enter units of shares paid for"),
  file: Yup.mixed().required("File is required"),
  method: Yup.string().required("Payment method is required"),
});

export const bulkUploadShareSchema = Yup.object().shape({
  identifier: Yup.string().required("identifier is required"),
  file: Yup.mixed().required("File is required"),
});

export const bulkUploadMembersSchema = Yup.object().shape({
  file: Yup.mixed().required("File is required"),
});

export const getLoanRequestInformationSchema = (
  memberContributionsDetails: SingleMemberContributionDetails,
  selectedLoanProduct?: LoanProduct
) => {
  return Yup.object().shape({
    loanproductID: Yup.string().required("Choose desired loan product"),
    loanAmount: Yup.string()
      .required("Loan amount is required")
      .test(
        "is-above-minimum",
        `Minimum loan amount is ${formatToCurrency(
          selectedLoanProduct?.MinAmount ?? 0,
          { currency: "NGN", decimalPlaces: 2 }
        )}`,
        (value) => {
          return (
            (parseStringToNumber(value) ?? 0) >
            (parseStringToNumber(selectedLoanProduct?.MinAmount) ?? 0)
          );
        }
      )
      .test(
        "is-below-maximum",
        `You are only eligible for up to ${formatToCurrency(
          ((selectedLoanProduct?.MaxGuarantor ?? 0) === 0
            ? 1
            : selectedLoanProduct?.XAmount ?? 0) *
            (parseStringToNumber(memberContributionsDetails.Balance) ?? 0),
          { currency: "NGN", decimalPlaces: 2 }
        )}`,
        (value) => {
          return (
            (parseStringToNumber(value) ?? 0) <=
            ((selectedLoanProduct?.MaxGuarantor ?? 0) === 0
              ? 1
              : selectedLoanProduct?.XAmount ?? 0) *
              (parseStringToNumber(memberContributionsDetails.Balance) ?? 0)
          );
        }
      ),
    disbursemode: Yup.string().required("Choose disbursal method"),
    disbursebankID: Yup.number().when(
      "disbursemode",
      ([disbursemode], schema) => {
        return disbursemode === "bank"
          ? schema.required("Choose disbursal bank account")
          : schema.notRequired();
      }
    ),
    tenor: Yup.string()
      .required("Loan tenor is required")
      .test(
        "is-within-range",
        `Loan tenor is between 1 to ${parseStringToNumber(
          selectedLoanProduct?.MaxTenure
        )} months`,
        (value) => {
          return (
            (parseStringToNumber(value) ?? 0) > 0 &&
            (parseStringToNumber(value) ?? 0) <=
              (parseStringToNumber(selectedLoanProduct?.MaxTenure) ?? 0)
          );
        }
      ),
  });
};

export const getGuarantorInformationSchema = (amountLeft: number) => {
  return Yup.object().shape({
    email: Yup.string().required("Choose a guarantor"),
    amount: Yup.string()
      .required("Loan amount is required")
      .test(
        "is-below-maximum",
        `Amount exceeds the required guarantor amount left (${formatToCurrency(
          amountLeft,
          { currency: "NGN", decimalPlaces: 2 }
        )}). Please adjust`,
        (value) => {
          return (parseStringToNumber(value) ?? 0) <= amountLeft;
        }
      ),
  });
};

export const loanRequestRepaymentDetails = Yup.object().shape({
  repaymentMode: Yup.string().required("Select repayment method"),
  repaymentMethodId: Yup.number().when(
    "repaymentMode",
    ([repaymentMode], schema) => {
      return repaymentMode === "card" || repaymentMode === "bank"
        ? schema.required("Repayment method is required")
        : schema.notRequired();
    }
  ),
});
